import { Link } from "react-router-dom";
import { createPortal } from "react-dom";
import { CheckCircle2, X, Smartphone, IdCard, FileText, Camera, Clock } from "lucide-react";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";

/**
 * GetVerifiedModal — Delta-style "Account Created!" celebration + KYC nudge.
 * Appears on /markets right after a fresh registration completes the mobile
 * OTP step, OR when a logged-in user with kyc_status != 'approved' lands
 * on /markets (dismissible per session).
 */
export default function GetVerifiedModal({ open, onClose, eta = "3 min" }) {
  useBodyScrollLock(open);

  if (!open) return null;

  return createPortal(
    <div
      className="cm-modal-overlay bg-black/45 backdrop-blur-[2px] cm-fade-up"
      onClick={onClose}
      data-testid="get-verified-modal"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cm-modal-panel relative w-full max-w-[440px] bg-card rounded-2xl shadow-2xl overflow-hidden"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="get-verified-close"
          className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-surface-2 hover:bg-line text-muted-foreground z-10"
        >
          <X size={15} />
        </button>

        <div className="px-7 pt-9 pb-6 text-center">
          <div className="mx-auto w-[88px] h-[88px] rounded-full bg-up/10 grid place-items-center ring-8 ring-emerald-50/60">
            <CheckCircle2 size={50} className="text-up" strokeWidth={2.4} />
          </div>

          <h2 className="mt-5 text-[22px] font-semibold text-foreground tracking-tight">
            Account Created!
          </h2>
          <p className="mt-1.5 text-[13.5px] text-muted-foreground">
            Get verified to start trading
          </p>

          <div className="mt-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border bg-card text-[12px] text-muted-foreground font-medium">
            <Clock size={12} />
            <span>ETA: {eta}</span>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-2.5">
            {[
              { Icon: Smartphone, label: "Phone" },
              { Icon: IdCard, label: "Aadhaar" },
              { Icon: FileText, label: "PAN" },
              { Icon: Camera, label: "Selfie" },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-surface-2 border border-border"
              >
                <Icon size={16} className="text-primary" />
                <span className="text-[13px] font-semibold text-foreground">{label}</span>
              </div>
            ))}
          </div>

          <Link
            to="/profile/kyc"
            data-testid="get-verified-continue"
            className="mt-6 w-full inline-flex items-center justify-center py-3.5 rounded-lg brand-gradient text-primary-foreground font-semibold text-[14.5px] hover:brightness-110 transition"
          >
            Continue
          </Link>

          <button
            type="button"
            onClick={onClose}
            data-testid="get-verified-skip"
            className="mt-3 w-full text-[12.5px] text-muted-foreground hover:text-foreground"
          >
            Skip for now — explore the exchange
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
